import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { SingleValue, MultiValue } from "react-select";
import Chip from "../../components/chip";
import EmptyDataOverlay from "../../components/empty-data-overlay";
import Loading from "../../components/loading";
import StyledSelect from "../../components/styled-select";
import Table from "../../components/table";
import TableBodyCell from "../../components/table-body-cell";
import ToastComponent from "../../components/toast";
import PermissionGate from "../../core/layouts/permission-gate";
import { isSelectSingleValue } from "../../core/utils";
import { adminCat } from "../../core/utils/constants";
import paths from "../../navigation/paths";
import { getErrorState, setBreadcrumbState } from "../../redux/features/common-slice";
import { getAllCustomersState, CustomerStatus, allCustomersStateAction, CustomerResponse } from "../../redux/features/customer-slice";
import { UserRole } from "../../redux/features/user-slice";
import { AppDispatch } from "../../redux/store";
import { OptionValue } from "../../types/component";

export default function CustomersPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allCustomersState = useSelector(getAllCustomersState);
  const errorState = useSelector(getErrorState);

  const [selectedStatuses, setSelectedStatuses] = useState<CustomerStatus[]>([]);

  const fetchAllCustomers = useCallback(async () => {
    await dispatch(
      allCustomersStateAction({ status: selectedStatuses })
    );
  }, [dispatch, selectedStatuses]);

  useEffect(() => {
    fetchAllCustomers();
  }, [fetchAllCustomers]);

  useEffect(() => {
    dispatch(setBreadcrumbState([{ id: "customers", path: null, title: "Customers" }]));
  }, [dispatch]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState, navigate]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Trainer]}
    >
      <Helmet title={"All Customers"} />
      <div className="flex items-center justify-end mb-4 space-x-2">
        <StyledSelect
          options={Object.values(CustomerStatus).map((e: string) => ({
            value: e,
            label: e.charAt(0).toUpperCase() + e.slice(1),
          }))}
          isClearable={false}
          value={{
            value: "",
            label:
              selectedStatuses.length > 0
                ? `${selectedStatuses.length} status${
                    selectedStatuses.length > 1 ? "es" : ""
                  } selected`
                : "All statuses",
          }}
          onChange={(
            value: SingleValue<OptionValue> | MultiValue<OptionValue>
          ) => {
            if (value && isSelectSingleValue(value)) {
              if (selectedStatuses.includes(value.value as CustomerStatus)) {
                setSelectedStatuses(
                  selectedStatuses.filter((e) => e !== value.value)
                );
              } else {
                setSelectedStatuses([...selectedStatuses, value.value as CustomerStatus]);
              }
            }
          }}
          exClassName="w-48 z-20"
          exOption={(value: OptionValue) => ({
            background:
              selectedStatuses.findIndex((e) => e === value.value) !== -1
                ? "#f1f5f9"
                : undefined,
          })}
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 64px)",
        }}
        headerCells={[
          { id: "name", label: "Name", align: "text-left", width: "w-5/12" },
          { id: "email", label: "Email", align: "text-left", width: "w-5/12" },
          { id: "status", label: "Status", align: "text-left", width: "w-2/12" },
        ]}
        body={
          allCustomersState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={4} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : allCustomersState.data.length > 0
            ? allCustomersState.data.map((customer: CustomerResponse, index: number) => (
                <tr
                  key={customer.id}
                  className="border-t border-dashed border-divider cursor-pointer hover:bg-action-hover"
                  onClick={() => navigate(`${paths.CUSTOMERS}/${customer.id}`)}
                >
                  <TableBodyCell exClassName="font-medium">
                    <label className="whitespace-normal break-words hover:underline cursor-pointer">
                      {customer.name}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    <label className="text-text-secondary text-xs font-light italic">
                      {customer.email || "--"}
                    </label>
                  </TableBodyCell>
                  <TableBodyCell>
                    {customer.status === CustomerStatus.Inactive ? (
                      <Chip
                        label={customer.status}
                        type="error"
                        exClassName="w-fit"
                      />
                    ) : (
                      <Chip
                        label={customer.status}
                        type="success"
                        exClassName="w-fit"
                      />
                    )}
                  </TableBodyCell>
                </tr>
              ))
            : [
                <tr key="empty-row">
                  <td colSpan={4} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Customers found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="allCustomers" />
    </PermissionGate>
  );
}